import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { roleBadgeClass } from '../../constants/roles';

/**
 * Felhasználó kiválasztása név/email szerint, késleltetett kereséssel.
 *
 * A kiválasztott fiókot chipként mutatja — így művelet indítása előtt egyértelmű,
 * KIRE fog vonatkozni. Ez olyan helyeken számít, ahol a tévedés valakinek az
 * előfizetését érinti.
 */
export default function UserPicker({ value, onChange, placeholder = 'Search by username or email…' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  const search = useCallback((q) => {
    clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); return; }

    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await api.get('/admin/users', { params: { search: q, limit: 8 } });
        setResults(res.data.users || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Kívülre kattintásra csukjuk a találati listát
  useEffect(() => {
    if (results.length === 0) return;
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setResults([]);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [results.length]);

  if (value) {
    return (
      <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg
                      bg-brand-500/[0.08] border border-brand-400/25">
        <span className="w-7 h-7 rounded-full bg-brand-gradient border border-brand-400/30
                         flex items-center justify-center text-[11px] font-bold text-white uppercase
                         flex-shrink-0">
          {value.username?.[0] || '?'}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{value.username}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5
                              rounded border flex-shrink-0 ${roleBadgeClass(value.role)}`}>
              {value.role}
            </span>
          </span>
          <span className="block text-xs text-gray-600 truncate">{value.email}</span>
        </span>
        <button
          onClick={() => { onChange(null); setQuery(''); }}
          className="btn-icon w-7 h-7 flex-shrink-0"
          aria-label="Clear selection"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" className="w-3.5 h-3.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round"
           className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none">
        <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
      </svg>
      <input
        type="text"
        className="input pl-10"
        placeholder={placeholder}
        value={query}
        onChange={e => { setQuery(e.target.value); search(e.target.value); }}
      />

      {(results.length > 0 || (loading && query.trim())) && (
        <div className="absolute z-20 mt-1.5 w-full rounded-xl overflow-hidden
                        bg-gray-900/95 backdrop-blur-xl border border-white/[0.09]
                        shadow-card-lg animate-scale-in">
          {loading && results.length === 0 ? (
            <p className="px-3.5 py-3 text-sm text-gray-600">Searching…</p>
          ) : results.map(u => (
            <button
              key={u.id}
              onMouseDown={() => { onChange(u); setResults([]); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left
                         hover:bg-white/[0.06] transition-colors"
            >
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-sm text-white truncate">{u.username}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5
                                    rounded border flex-shrink-0 ${roleBadgeClass(u.role)}`}>
                    {u.role}
                  </span>
                </span>
                <span className="block text-xs text-gray-600 truncate">{u.email}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
